import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { CiSearch } from "react-icons/ci";
import { HiOutlinePlusSm } from "react-icons/hi";
import { LiaEditSolid } from "react-icons/lia";
import { RiDeleteBin4Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

export const AdminManageWarmupRoutineTemplates = () => {
  const navigate = useNavigate(); // Initialize navigation hook

  const { staff } = useStaffAuthContext();
  const [warmups, setWarmups] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [templateTitle, setTemplateTitle] = useState("");
  const [deletePopup, setDeletePopup] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Fetch warmup templates
  const fetchTemplates = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_URL.ADMIN_SPEED_TEMPLATE_WARMUP_ROUTINE_MANAGE}?page=${currentPage}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok) {
        setWarmups(data.data);
        setTotalPages(data.totalPages);
      } else {
        setError(data.message || "Failed to fetch templates.");
      }
    } catch (err) {
      setError("Error fetching templates.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (staff.token) {
      fetchTemplates();
    }
  }, [staff.token, currentPage, itemsPerPage]);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  const filteredWarmups = warmups.filter((warmup) =>
    warmup.template_title.toLowerCase().includes(searchQuery),
  );

  const handleDelete = async () => {
    if (!selectedTemplateId) return;

    try {
      const response = await fetch(
        `${API_URL.ADMIN_DELETE_SPEED_TEMPLATE_WARMUP_ROUTINE_TEMPLATE}${selectedTemplateId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );

      if (response.ok) {
        setDeletePopup(false);
        setSelectedTemplateId(null);
        alert("Template deleted successfully");
        fetchTemplates(); // Refresh list
      } else {
        alert("Failed to delete template.");
      }
    } catch (error) {
      alert("Error deleting template.");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleCreateTemplate = async () => {
    //if (!templateTitle.trim()) return;

    try {
      const response = await fetch(
        API_URL.ADMIN_POST_SPEED_TEMPLATE_WARMUP_ROUTINE_NEW,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${staff.token}`,
          },
          body: JSON.stringify({ template_title: templateTitle }),
        },
      );

      const data = await response.json();
      console.log(data);
      if (response.ok) {
        setShowPopup(false);
        setTemplateTitle("");
        alert("New template created");
        // Redirect to the edit screen for the newly created template
        if (data?.template_id) {
          navigate(
            `/administrator/template-speed/warmup-routine/edit/${data.template_id}`,
          );
        } else {
          fetchTemplates(); // Fallback in case ID is missing
        }
      } else if (response.status === 400) {
        alert(
          data.message || "Invalid input. Please check the template title.",
        );
      } else {
        alert(data.message || "Failed to create template. Please try again.");
      }
    } catch (error) {
      alert("Error creating template");
    }
  };

  return (
    <div className="flex md:h-screen font-inter">
      <StaffSideMenu />
      <div className="max-md:w-full md:flex-1 md:flex md:flex-col md:overflow-hidden md:overflow-y-auto bg-bgBase">
        <div className="bg-white space-y-6 pt-5 pb-3 md:py-6 px-5 md:px-10 xl:px-14">
          <div className="w-full max-w-[1336px]">
            <div className="flex flex-col md:justify-between gap-6">
              <h1 className="font-inter font-medium text-dark text-xl md:text-2xl xl:text-[28px] !leading-none max-md:pl-12">
                Manage Warmup Routine Templates
              </h1>

                <div className="flex flex-wrap md:justify-between md:items-center gap-3">
                  <div className="relative w-full min-w-64 max-w-64">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearch}
                      placeholder="Search warmups..."
                      className="w-full p-3 border border-black/10 rounded-lg font-inter font-normal leading-3 text-sm pl-10"
                    />
                    <span className="absolute top-1/2 -translate-y-1/2 left-3">
                      <CiSearch color="#666666" size={20} />
                    </span>
                  </div>
                  <button
                    onClick={() => setShowPopup(true)}
                    className="px-6 py-3 flex items-center gap-2 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                  >
                    <HiOutlinePlusSm color="#fff" size={18} /> Create Warmup
                    Template
                  </button>
                </div>
            </div>
          </div>
        </div>

        <div className="py-10 px-5 md:px-8 xl:px-16">
          <div className="w-full max-w-[1336px]">
            {error && (
              <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
                {error}
              </div>
            )}
            {isLoading ? (
              <div className="bg-white w-48 rounded-md p-2">
                <p className="text-dark font-normal text-sm">Loading...</p>
              </div>
            ) : (
              <div className="overflow-auto show-scrollbar max-h-[650px]">
                <div className="rounded-10 border border-tblBorder w-full">
                  <table className="table-auto table-radius w-full">
                    <thead className="sticky top-0 bg-tblHead border-b border-tblBorder">
                      <tr className="text-dark font-inter !font-medium text-xs md:text-sm capitalize">
                        <th className="px-6 py-3 text-left">Title</th>
                        <th className="px-6 py-3 text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-tblBorder">
                      {filteredWarmups.length > 0 ? (
                        filteredWarmups.map((warmup, index) => (
                          <tr
                            key={warmup._id}
                            className={`text-dark text-sm font-normal`}
                          >
                            <td className="px-6 py-4">
                              {warmup.template_title}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex justify-end items-center gap-2">
                                <Link
                                  to={`/administrator/template-speed/warmup-routine/edit/${warmup._id}`}
                                  className="p-2 inline-block rounded-md hover:bg-slate-100"
                                >
                                  <LiaEditSolid color="#666666" size={20} />
                                </Link>
                                <button
                                  className="p-2 rounded-md hover:bg-slate-100"
                                  onClick={() => {
                                    setSelectedTemplateId(warmup._id);
                                    setDeletePopup(true);
                                  }}
                                >
                                  <RiDeleteBin4Line color="#666666" size={20} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center py-4">
                            No templates found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="bg-white pt-4 md:py-6 px-5 md:px-10 xl:px-14 mt-auto">
          <div className="w-full max-w-[1336px]">
              <div className="flex justify-between items-center">
                <button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm ${
                    currentPage === 1 ? "cursor-not-allowed" : "text-black"
                  }`}
                >
                  <MdKeyboardArrowLeft size={20} /> Previous
                </button>
                <span className="text-dark font-inter font-normal text-sm ">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm ${
                    currentPage === totalPages
                      ? "cursor-not-allowed"
                      : "text-black"
                  }`}
                >
                  Next <MdKeyboardArrowRight size={20} />
                </button>
              </div>
          </div>
        </div>
      </div>

      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 px-5">
          <div className="bg-white p-6 rounded-10 shadow-lg w-full max-w-md">
            <h2 className="text-lg font-medium mb-4">
              Create Warmup Template
            </h2>
            <input
              type="text"
              value={templateTitle}
              onChange={(e) => setTemplateTitle(e.target.value)}
              placeholder="Enter template title"
              className="w-full p-3 border border-black/10 rounded-lg font-inter font-normal text-sm leading-none mb-4"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowPopup(false)}
                className="bg-transparent border border-tblBorder rounded-10 py-2.5 px-5 text-dark font-inter font-medium text-sm leading-tight capitalize hover:bg-dark hover:text-white transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTemplate}
                className="bg-dark border border-tblBorder rounded-10 py-2.5 px-5 text-white font-inter font-medium text-sm leading-tight capitalize hover:bg-black transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {deletePopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h2 className="text-lg font-semibold mb-4">Are you sure?</h2>
            <p>Do you really want to delete this template?</p>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setDeletePopup(false)}
                className="px-4 py-2 bg-gray-400 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
